import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, delay, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ConnectionTypesResource } from '../resources/connection-types.resource';
import {
  getConnectionsList,
  getConnectionsListResponse,
  loadMoreConnectionsList,
  loadMoreConnectionsListResponse,
  removeConnection,
  removeConnectionError,
  removeConnectionItem,
  removeConnectionResponse,
  saveConnection,
  saveConnectionError,
  saveConnectionResponse,
  searchConnectionsList,
  searchConnectionsListResponse,
  updateConnection,
  updateConnectionError,
  updateConnectionResponse,
  getConnectionTypes,
  getConnectionTypesResponse,
  closeConnectionsModal,
  testConnection,
  testConnectionResponse,
  testConnectionError,
  getConnectionItem,
  getConnectionItemResponse,
  getConnectionsError,
  getConnectionError,
  getConnectionTypesError,
  getConnectionSchema,
  getConnectionSchemaError,
  getConnectionSchemaResponse,
} from './connections.actions';
import { SLIDER_CLOSE_ANIMATION_DURATION } from '../../constants/animation-constants';
import { ConnectionsResource } from '../resources/connections.resource';
import { ConnectionItemsResource } from '../resources/connection-items.resource';
import { AppState } from '../../store';
import { selectConnections } from './connections.selectors';
import { NotifyService } from '../../common/services/notify.service';

function handleTestError(res) {
  let message;
  if (res.error_message) {
    message = res.error_message;
  } else if (res.data && res.data.errors) {
    const element = $('[name=connectionForm]');
    const validationType = element.parent().attr('type');

    const errors = res.data.errors.map((e) => {
      e.fieldNameKey = e.field !== 'base' ? [validationType, 'labels', e.field].join('.') : '';
      return e;
    });

    message = errors
      .map((error) => {
        const wrapper = document.createElement('span');
        const key = this.translate.instant(error.fieldNameKey);

        if (key !== 'base') {
          const childElement = document.createElement('strong');
          childElement.innerText = `${key} `;
          wrapper.appendChild(childElement);
        }
        const childElement = document.createElement('span');
        childElement.innerText = error.message;
        wrapper.appendChild(childElement);
        return wrapper.innerHTML;
      })
      .join('<br>');
  } else if (res.error && res.error.error_message) {
    message = res.error.error_message;
  } else if (res.status === -1) {
    message = this.translate.instant('response.timeout.message');
  } else {
    message = `${res.status}`;
  }
  return message;
}

@Injectable({
  providedIn: 'root',
})
export class ConnectionsEffects {
  @Effect()
  getTypes$ = this.actions$.pipe(
    ofType(getConnectionTypes),
    switchMap(() =>
      this.connectionTypesResource.query().pipe(
        map((types) => getConnectionTypesResponse({ types })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getConnectionTypesError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  getConnections$ = this.actions$.pipe(
    ofType(getConnectionsList),
    switchMap(({ params }) =>
      this.connectionsResource.query(params).pipe(
        map((connections) => getConnectionsListResponse({ connections })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getConnectionsError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  getConnection$ = this.actions$.pipe(
    ofType(getConnectionItem),
    switchMap(({ connectionType, connectionId }) =>
      this.connectionItemsResource.get(connectionType, connectionId).pipe(
        map((connection) => getConnectionItemResponse({ connection })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getConnectionError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  searchConnections$ = this.actions$.pipe(
    ofType(searchConnectionsList),
    switchMap(({ params }) =>
      this.connectionsResource.search(params).pipe(
        map((connections) => searchConnectionsListResponse({ connections })),
        catchError((error) => {
          this.notify.error(this.translate.instant(`response.${error.status}.message`));
          return of(getConnectionsError({ error }));
        }),
      ),
    ),
  );

  @Effect()
  loadMoreConnections$ = this.actions$.pipe(
    ofType(loadMoreConnectionsList),
    switchMap(({ params }) =>
      this.connectionsResource
        .query(params)
        .pipe(map((connections) => loadMoreConnectionsListResponse({ connections }))),
    ),
  );

  @Effect()
  saveConnection$ = this.actions$.pipe(
    ofType(saveConnection),
    switchMap(({ connection, connectionType }) =>
      this.connectionItemsResource.save(connectionType, connection, { include: 'package,cluster,owner' } as any).pipe(
        map((connectionResponse) => saveConnectionResponse({ data: connectionResponse })),
        catchError((response) => of(saveConnectionError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  saveConnectionResponse$ = this.actions$.pipe(
    ofType(saveConnectionResponse),
    map(() => closeConnectionsModal()),
  );

  @Effect()
  updateConnection$ = this.actions$.pipe(
    ofType(updateConnection),
    withLatestFrom(this.store.select(selectConnections)),
    switchMap(([{ connection, params, connectionId, connectionType }, connections]) =>
      this.connectionItemsResource
        .update(connectionType, connectionId, connection, { ...params, include: 'packages' } as any)
        .pipe(
          map((connectionResponse) => {
            const previousConnection = connections.find((item) => item.id === connectionResponse.id) || ({} as any);
            const shouldNotCloseModal =
              (previousConnection.tunnel_type !== 'reverse' && connectionResponse.tunnel_type === 'reverse') ||
              (previousConnection.tunnel_type !== 'ssh-tunnel' && connectionResponse.tunnel_type === 'ssh-tunnel');
            return updateConnectionResponse({ data: connectionResponse, shouldCloseModal: !shouldNotCloseModal });
          }),
          catchError((response) => of(updateConnectionError({ error: response.error }))),
        ),
    ),
  );

  @Effect()
  testConnection$ = this.actions$.pipe(
    ofType(testConnection),
    switchMap(({ testData, connectionId, connectionType }) =>
      this.connectionItemsResource.test(connectionType, connectionId, testData).pipe(
        map(() =>
          testConnectionResponse({
            message: [this.translate.instant('connections.form.default.notifications.test.success_message')],
          }),
        ),
        catchError((error) =>
          of(
            testConnectionError({
              errors: [
                {
                  message: this.translate.instant('connections.form.default.notifications.test.error_message', {
                    message: handleTestError(error),
                  }),
                },
              ],
            }),
          ),
        ),
      ),
    ),
  );

  @Effect()
  updateConnectionResponse$ = this.actions$.pipe(
    ofType(updateConnectionResponse),
    map(({ shouldCloseModal }) => (shouldCloseModal ? closeConnectionsModal() : { type: 'empty' })),
  );

  @Effect()
  removeConnection$ = this.actions$.pipe(
    ofType(removeConnection),
    switchMap(({ connectionId, connectionType }) =>
      this.connectionItemsResource.remove(connectionType, connectionId).pipe(
        map((connectionResponse) => removeConnectionItem({ data: connectionResponse })),
        catchError((response) => of(removeConnectionError({ error: response.error }))),
      ),
    ),
  );

  @Effect()
  removeConnectionResponse$ = this.actions$.pipe(
    ofType(removeConnectionItem),
    delay(SLIDER_CLOSE_ANIMATION_DURATION),
    map(({ data }) => removeConnectionResponse({ data })),
  );

  @Effect()
  getConnectionSchema$ = this.actions$.pipe(
    ofType(getConnectionSchema),
    switchMap(({ connectionId, connectionType, schemaRequestData, onlyData }) =>
      this.connectionItemsResource.schema(connectionType, connectionId, schemaRequestData).pipe(
        map((response) =>
          getConnectionSchemaResponse({
            onlyData,
            connectionSchemaData: response,
            connectionSchema: response.fields.map((field) => ({
              name: Object.keys(field)[0],
              data_type: Object.values(field)[0],
              alias: Object.keys(field)[0],
              category: 'Schema',
            })),
          }),
        ),
        catchError((response) => of(getConnectionSchemaError({ error: response.error }))),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private connectionTypesResource: ConnectionTypesResource,
    private connectionItemsResource: ConnectionItemsResource,
    private connectionsResource: ConnectionsResource,
    private translate: TranslateService,
    private store: Store<AppState>,
    private notify: NotifyService,
  ) {}
}
